import { PageHeader } from "@finitless/shared/components";
import { Button } from "../../../ui/UIElements/Button";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../../ui/UIElements/Icon";
import Pagination from "../../../ui/Data/Pagination";
import CampaignsLoading from "../../../ui/Campaigns/CampaignsLoading";
import CampaignsError from "../../../ui/Campaigns/CampaignsError";
import {
  useCartRecoveryRules,
  useUpdateCartRecoveryRule,
} from "../../../../hooks/useCartRecovery";
import { useUrlStates, useToast } from "@finitless/shared/hooks";
import { handleApiError } from "@finitless/shared/lib";
import { Switch } from "../../../ui/UIElements/Switch";
import "./cart-recovery-ordering.css";
import {
  CAMPAIGNS_PER_PAGE,
  MAX_VISIBLE_PAGES,
} from "../../../../constants/campaignConstants";

const truncateText = (text, maxLen = 72) => {
  if (!text || typeof text !== "string") return "";
  const trimmed = text.replace(/\s+/g, " ").trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen)}…`;
};

const getPrimaryDelayMinutes = (rule) => {
  const times = rule?.times;
  if (!Array.isArray(times) || times.length === 0) return null;
  const first = times[0];
  return typeof first === "number" ? first : null;
};

const CartRecoveryPage = () => {
  const { t } = useTranslation("cartRecovery");
  const { t: tCommon } = useTranslation(["common", "agentTranslations"]);
  const navigate = useNavigate();
  const toast = useToast();
  const updateRuleMutation = useUpdateCartRecoveryRule();

  const initialUrlStates = useMemo(
    () => ({
      searchTerm: "",
      currentPage: "1",
    }),
    [],
  );

  const [, updateUrlState] = useUrlStates(initialUrlStates);

  const [searchInput, setSearchInput] = useState("");
  const searchInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [togglingRuleId, setTogglingRuleId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      updateUrlState("searchTerm", searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, updateUrlState]);

  const { data, isLoading, error, refetch } = useCartRecoveryRules({
    page: currentPage,
    pageSize: CAMPAIGNS_PER_PAGE,
    searchTerm,
  });

  const rules = data?.rules || [];
  const pagination = data?.pagination || {};

  const calculatedTotalPages = pagination?.total
    ? Math.ceil(pagination.total / CAMPAIGNS_PER_PAGE)
    : 1;

  const totalPages = pagination?.total_pages || calculatedTotalPages;

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
    searchInputRef.current?.focus();
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlState("searchTerm", "");
    updateUrlState("currentPage", "1");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", String(page));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (rule) => {
    navigate(`/growth/cart-recovery/${rule.id}`, { state: { rule } });
  };

  const handleCreateRule = () => {
    navigate("/growth/cart-recovery/new");
  };

  const handleRuleEnabledChange = async (rule, enabled) => {
    if (rule.enabled === enabled) return;
    setTogglingRuleId(rule.id);
    try {
      await updateRuleMutation.mutateAsync({
        ruleId: rule.id,
        data: { enabled },
      });
    } catch (error) {
      handleApiError(error, toast, {
        title: tCommon("states.error"),
        defaultMessage: t("toggle.error"),
      });
    } finally {
      setTogglingRuleId(null);
    }
  };

  const formatDelay = (rule) => {
    const minutes = getPrimaryDelayMinutes(rule);
    if (minutes == null) return "—";
    return t("delay.minutesShort", { count: minutes });
  };

  return (
    <div
      className="ordering-cart-recovery-surface space-y-6"
      data-ordering-expression="product"
    >
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />
        <div className="flex items-center gap-2">
          <Button
            emphasis="solid"
            intent="brand"
            size="touch"
            type="button"
            onClick={handleCreateRule}
            className="space-x-2 px-3 py-2 sm:px-4"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span>{t("actions.newRule")}</span>
          </Button>
        </div>
      </div>

      <div className="border-border bg-background rounded-xl border p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="min-w-0 flex-1">
            <div className="ordering-cart-recovery-search relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="text-muted-foreground h-4 w-4" />
              </div>
              <input
                ref={searchInputRef}
                type="search"
                autoComplete="off"
                placeholder={t("search.placeholder")}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="border-border bg-card placeholder:text-muted-foreground focus:ring-ring text-foreground h-10 w-full rounded-lg border px-4 py-2 pr-10 pl-10 focus:border-transparent focus:ring-2 focus:outline-none"
                aria-label={t("search.placeholder")}
              />
              {searchInput ? (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  aria-label={tCommon("actions.clearSearch", {
                    ns: "agentTranslations",
                  })}
                  className="bg-muted hover:bg-muted absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform items-center justify-center rounded-full transition-colors"
                >
                  <Icon name="X" className="text-muted-foreground h-3 w-3" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <CampaignsLoading />
      ) : error ? (
        <CampaignsError error={error} onRetry={refetch} />
      ) : rules.length > 0 ? (
        <>
          <div className="border-border bg-background overflow-x-auto rounded-xl border">
            <table
              className="w-full min-w-[700px]"
              style={{ tableLayout: "fixed" }}
            >
              <colgroup>
                <col style={{ width: "32%" }} />
                <col style={{ width: "38%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <thead>
                <tr className="border-border border-b">
                  <th className="text-muted-foreground px-4 py-4 text-left text-xs font-medium tracking-wider uppercase sm:px-6">
                    {t("table.columns.name")}
                  </th>
                  <th className="text-muted-foreground px-4 py-4 text-left text-xs font-medium tracking-wider uppercase sm:px-6">
                    {t("table.columns.description")}
                  </th>
                  <th className="text-muted-foreground px-4 py-4 text-left text-xs font-medium tracking-wider uppercase sm:px-6">
                    {t("table.columns.delay")}
                  </th>
                  <th className="text-muted-foreground px-4 py-4 text-center text-xs font-medium tracking-wider uppercase sm:px-6">
                    {t("table.columns.status")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border/50 divide-y">
                {rules.map((rule) => {
                  const name =
                    rule.name?.trim() || t("table.fallback.untitled");
                  const description = truncateText(rule.description || "");

                  return (
                    <tr
                      key={rule.id}
                      onClick={() => handleViewDetails(rule)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleViewDetails(rule);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      className="hover:bg-card/50 focus:bg-card/50 cursor-pointer transition-colors focus:outline-none"
                      aria-label={name}
                    >
                      <td className="overflow-hidden px-4 py-4 sm:px-6">
                        <div
                          className="text-foreground truncate text-sm font-semibold sm:text-base"
                          title={name}
                        >
                          {name}
                        </div>
                      </td>
                      <td className="overflow-hidden px-4 py-4 sm:px-6">
                        <div
                          className="text-muted-foreground truncate text-sm"
                          title={rule.description || ""}
                        >
                          {description || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-4 sm:px-6">
                        <span className="text-muted-foreground text-sm">
                          {formatDelay(rule)}
                        </span>
                      </td>
                      <td
                        className="px-4 py-4 text-center sm:px-6"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-col items-center justify-center gap-1.5 sm:flex-row sm:gap-2">
                          <Switch
                            checked={rule.enabled !== false}
                            disabled={togglingRuleId === rule.id}
                            onCheckedChange={(checked) =>
                              handleRuleEnabledChange(rule, checked)
                            }
                            aria-label={t("toggle.aria")}
                          />
                          <span
                            className={`text-xs font-medium whitespace-nowrap ${
                              rule.enabled
                                ? "text-success"
                                : "text-muted-foreground"
                            }`}
                          >
                            {rule.enabled
                              ? t("status.enabled")
                              : t("status.disabled")}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="border-border bg-background sticky bottom-0 z-[var(--layer-sticky)] mt-4 flex flex-col items-center justify-between gap-4 rounded-xl border p-4 shadow-[var(--od-shadow-2)] sm:flex-row">
              <div className="text-muted-foreground text-sm">
                {t("pagination.showing", {
                  from: (currentPage - 1) * CAMPAIGNS_PER_PAGE + 1,
                  to: Math.min(
                    currentPage * CAMPAIGNS_PER_PAGE,
                    pagination?.total || 0,
                  ),
                  total: pagination?.total || 0,
                })}
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxVisiblePages={MAX_VISIBLE_PAGES}
                disabled={isLoading}
              />
            </div>
          ) : null}
        </>
      ) : (
        <div className="border-border bg-background rounded-xl border p-12 text-center">
          <div className="bg-accent/20 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <Icon name="ShoppingCart" className="text-primary h-8 w-8" />
          </div>
          <h3 className="text-foreground mb-2 text-lg font-medium">
            {t("empty.title")}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchTerm
              ? t("empty.subtitleSearch")
              : t("empty.subtitleDefault")}
          </p>
          <Button
            emphasis="solid"
            intent="brand"
            size="touch"
            type="button"
            onClick={handleCreateRule}
            className="space-x-2 px-4 py-2"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span>{t("actions.createRule")}</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default CartRecoveryPage;
